// SPDX-License-Identifier: AGPL-3.0-or-later

import {create, fromBinary, toBinary, toJson} from '@bufbuild/protobuf';
import {
	type SyncedPreferences,
	SyncedPreferencesSchema,
} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/preferences_pb';
import {base64ToUint8Array, uint8ArrayToBase64} from 'uint8array-extras';

export type {SyncedPreferences} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/preferences_pb';
export {SyncedPreferencesSchema} from '@fluxer/schema/src/gen/fluxer/user/preferences/v1/preferences_pb';

export const EMPTY_SYNCED_PREFERENCES_ENCODED = '';
export const SYNCED_PREFERENCES_MAX_BYTES = 512 * 1024;
export const SYNCED_PREFERENCES_MAX_ENCODED_LENGTH = Math.ceil(SYNCED_PREFERENCES_MAX_BYTES / 3) * 4;
const BASE64_PATTERN = /^[A-Za-z0-9+/_-]*={0,2}$/;

export function isValidSyncedPreferencesEncoding(value: string): boolean {
	if (value === '') return true;
	if (value.length > SYNCED_PREFERENCES_MAX_ENCODED_LENGTH) return false;
	return BASE64_PATTERN.test(value);
}

export function createEmptySyncedPreferences(): SyncedPreferences {
	return create(SyncedPreferencesSchema);
}

export function encodeSyncedPreferences(preferences: SyncedPreferences): string {
	// pf_schema_version is a wire-level marker, not part of the caller-facing message: it is
	// stamped here and stripped on decode, so callers never see or set it. Every non-empty blob
	// this build writes carries it, which is what makes the one-shot renumber migration safe to
	// gate on its absence.
	const bytes = toBinary(SyncedPreferencesSchema, {...preferences, pfSchemaVersion: 0});
	if (bytes.byteLength === 0) return EMPTY_SYNCED_PREFERENCES_ENCODED;
	return uint8ArrayToBase64(concatBytes(bytes, encodeVarintField(PF_SCHEMA_VERSION_FIELD, PF_SCHEMA_VERSION)));
}

export function canonicalizeSyncedPreferencesEncoding(encoded: string): string {
	return encodeSyncedPreferences(decodeSyncedPreferences(encoded));
}

export function encodedSyncedPreferencesByteLength(encoded: string): number {
	if (encoded === '') return 0;
	const padding = encoded.endsWith('==') ? 2 : encoded.endsWith('=') ? 1 : 0;
	return Math.floor((encoded.length * 3) / 4) - padding;
}

export function decodeSyncedPreferences(encoded: string | null | undefined): SyncedPreferences {
	if (!encoded) return createEmptySyncedPreferences();
	let bytes: Uint8Array;
	try {
		bytes = base64ToUint8Array(encoded);
	} catch (error) {
		throw new SyncedPreferencesDecodeError(
			error instanceof Error ? `invalid base64: ${error.message}` : 'invalid base64',
		);
	}
	if (bytes.byteLength === 0) return createEmptySyncedPreferences();
	try {
		const decoded = fromBinary(SyncedPreferencesSchema, migrateLegacyPopularFusionFields(bytes));
		decoded.pfSchemaVersion = 0;
		return decoded;
	} catch (error) {
		throw new SyncedPreferencesDecodeError(
			error instanceof Error ? error.message : 'invalid synced preferences protobuf',
		);
	}
}

export function decodeSyncedPreferencesLenient(encoded: string | null | undefined): SyncedPreferences {
	try {
		return decodeSyncedPreferences(encoded);
	} catch {
		return createEmptySyncedPreferences();
	}
}

export function syncedPreferencesToJson(preferences: SyncedPreferences): unknown {
	return toJson(SyncedPreferencesSchema, preferences);
}

export class SyncedPreferencesDecodeError extends Error {
	constructor(message: string) {
		super(`failed to decode synced_preferences: ${message}`);
		this.name = 'SyncedPreferencesDecodeError';
	}
}

const WIRE_VARINT = 0;
const WIRE_64BIT = 1;
const WIRE_LEN = 2;
const WIRE_32BIT = 5;

/** SyncedPreferences.pf_schema_version — present only once a blob has been renumbered. */
const PF_SCHEMA_VERSION_FIELD = 900;
const PF_SCHEMA_VERSION = 1;
/** SyncedPreferences field numbers UnreadCatchUpSettings used before upstream claimed them. */
const LEGACY_CATCH_UP_FIELDS = [114, 116];
const UNREAD_CATCH_UP_FIELD = 901;
/** SyncedPreferences.accessibility, whose payload holds the flag below. */
const ACCESSIBILITY_FIELD = 1;
/** AccessibilitySettings.custom_theme_css_sync_across_devices, before and after the renumber. */
const LEGACY_THEME_CSS_SYNC_FIELD = 65;
const THEME_CSS_SYNC_FIELD = 900;

/**
 * Move Popular Fusion's fields off the numbers upstream has since claimed, exactly once.
 *
 * PF stored UnreadCatchUpSettings at SyncedPreferences 114, then 116; upstream now uses 114 for
 * ReactionEmoji (double-tap) and 116 for AnnouncementPromptsState. It also reserved
 * AccessibilitySettings 65, which held PF's custom_theme_css_sync_across_devices. Both PF fields
 * now live at 900+.
 *
 * The 116 collision cannot be resolved by inspecting the payload: AnnouncementPromptsState's
 * `bool hide_publish_nudge = 1` and UnreadCatchUpSettings' `bool advance_last_read_on_scroll = 1`
 * are byte-identical on the wire (`08 01`). What disambiguates them is a fact about this
 * deployment rather than the bytes: PF's build has no announcement-prompts feature, so every
 * field-116 blob already in the database is catch-up data. Ambiguity would only begin once this
 * build can write 116 itself — which is precisely when pf_schema_version starts being present.
 *
 * So: no field 900 means a blob written before this sync, and 114/116/65 are rewritten
 * unconditionally. Field 900 present means already renumbered, and those numbers are left alone
 * forever. Field 65 carries the same caveat as 116 (upstream once had `double_click_message_actions`
 * there, also a bool) and is resolved the same way.
 */
export function migrateLegacyPopularFusionFields(bytes: Uint8Array): Uint8Array {
	if (bytes.byteLength === 0) return bytes;
	if (hasTopLevelField(bytes, PF_SCHEMA_VERSION_FIELD)) return bytes;
	const chunks: Array<Uint8Array> = [];
	let offset = 0;
	let changed = false;
	while (offset < bytes.length) {
		const keyStart = offset;
		const key = readVarint(bytes, offset);
		offset = key.next;
		const fieldNumber = key.value >>> 3;
		const wireType = key.value & 7;
		const valueStart = offset;
		offset = skipField(bytes, offset, wireType);
		if (wireType === WIRE_LEN && LEGACY_CATCH_UP_FIELDS.includes(fieldNumber)) {
			const length = readVarint(bytes, valueStart);
			chunks.push(encodeLengthDelimitedField(UNREAD_CATCH_UP_FIELD, bytes.subarray(length.next, offset)));
			changed = true;
			continue;
		}
		if (wireType === WIRE_LEN && fieldNumber === ACCESSIBILITY_FIELD) {
			const length = readVarint(bytes, valueStart);
			const payload = bytes.subarray(length.next, offset);
			const rewritten = renumberAccessibilityThemeCssSync(payload);
			if (rewritten !== payload) {
				chunks.push(encodeLengthDelimitedField(ACCESSIBILITY_FIELD, rewritten));
				changed = true;
				continue;
			}
		}
		chunks.push(bytes.subarray(keyStart, offset));
	}
	if (!changed) return bytes;
	chunks.push(encodeVarintField(PF_SCHEMA_VERSION_FIELD, PF_SCHEMA_VERSION));
	return concatBytes(...chunks);
}

function renumberAccessibilityThemeCssSync(payload: Uint8Array): Uint8Array {
	const chunks: Array<Uint8Array> = [];
	let offset = 0;
	let changed = false;
	while (offset < payload.length) {
		const keyStart = offset;
		const key = readVarint(payload, offset);
		offset = key.next;
		const fieldNumber = key.value >>> 3;
		const wireType = key.value & 7;
		const valueStart = offset;
		offset = skipField(payload, offset, wireType);
		if (fieldNumber === LEGACY_THEME_CSS_SYNC_FIELD && wireType === WIRE_VARINT) {
			chunks.push(encodeVarintField(THEME_CSS_SYNC_FIELD, readVarint(payload, valueStart).value));
			changed = true;
			continue;
		}
		chunks.push(payload.subarray(keyStart, offset));
	}
	if (!changed) return payload;
	return concatBytes(...chunks);
}

function hasTopLevelField(bytes: Uint8Array, wanted: number): boolean {
	let offset = 0;
	while (offset < bytes.length) {
		const key = readVarint(bytes, offset);
		offset = skipField(bytes, key.next, key.value & 7);
		if (key.value >>> 3 === wanted) return true;
	}
	return false;
}

function encodeVarintField(fieldNumber: number, value: number): Uint8Array {
	return concatBytes(encodeVarint((fieldNumber << 3) | WIRE_VARINT), encodeVarint(value));
}

function encodeLengthDelimitedField(fieldNumber: number, payload: Uint8Array): Uint8Array {
	return concatBytes(encodeVarint((fieldNumber << 3) | WIRE_LEN), encodeVarint(payload.byteLength), payload);
}

function encodeVarint(value: number): Uint8Array {
	const out: Array<number> = [];
	let remaining = value >>> 0;
	while (remaining > 0x7f) {
		out.push((remaining & 0x7f) | 0x80);
		remaining >>>= 7;
	}
	out.push(remaining);
	return Uint8Array.from(out);
}

function concatBytes(...parts: Array<Uint8Array>): Uint8Array {
	const total = parts.reduce((sum, part) => sum + part.byteLength, 0);
	const out = new Uint8Array(total);
	let cursor = 0;
	for (const part of parts) {
		out.set(part, cursor);
		cursor += part.byteLength;
	}
	return out;
}

function readVarint(bytes: Uint8Array, offset: number): {value: number; next: number} {
	let result = 0;
	let shift = 0;
	let pos = offset;
	while (pos < bytes.length) {
		const byte = bytes[pos++]!;
		result |= (byte & 0x7f) << shift;
		if ((byte & 0x80) === 0) return {value: result >>> 0, next: pos};
		shift += 7;
		if (shift > 35) throw new Error('varint too long');
	}
	throw new Error('truncated varint');
}

function skipField(bytes: Uint8Array, offset: number, wireType: number): number {
	switch (wireType) {
		case WIRE_VARINT:
			return readVarint(bytes, offset).next;
		case WIRE_64BIT:
			return offset + 8;
		case WIRE_32BIT:
			return offset + 4;
		case WIRE_LEN: {
			const length = readVarint(bytes, offset);
			const end = length.next + length.value;
			if (end > bytes.length) throw new Error('truncated length-delimited field');
			return end;
		}
		default:
			throw new Error(`unsupported wire type ${wireType}`);
	}
}
